/**
 * AdminCouponsPage — Manage discount coupon codes.
 *
 * /admin/coupons
 *   - List + search
 *   - Create / Edit / Delete
 *   - Usage counter
 *   - Validity window + scope
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit3, Trash2, Tag, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm";

export default function AdminCouponsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/coupons");
            setItems(r.data || []);
        } finally { setLoading(false); }
    };

    const handleSave = async (data) => {
        if (data.id) await api.put(`/admin/coupons/${data.id}`, data);
        else await api.post("/admin/coupons", data);
        setEditing(null);
        load();
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Bu kuponu silmek istediğinize emin misiniz?")) return;
        await api.delete(`/admin/coupons/${id}`);
        load();
    };

    return (
        <AdminLayout
            title="Kuponlar / Promosyon Kodları"
            action={
                <button data-testid="new-coupon-btn" onClick={() => setEditing({})} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">
                    <Plus className="h-4 w-4" /> Yeni Kupon
                </button>
            }
        >
            {loading ? <div>Yükleniyor...</div> : items.length === 0 ? (
                <div className="p-10 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
                    <Tag className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 text-sm mb-4">Henüz kupon yok. İlk indirim kodunuzu oluşturun (örn. <code className="text-sky-600">YAZ25</code>).</p>
                    <button onClick={() => setEditing({})} className="px-5 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">+ İlk Kupon</button>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-sm" data-testid="coupons-table">
                        <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Kod</th>
                                <th className="px-4 py-3 text-left">Açıklama</th>
                                <th className="px-4 py-3 text-right">İndirim</th>
                                <th className="px-4 py-3 text-right">Min. Tutar</th>
                                <th className="px-4 py-3 text-center">Geçerlilik</th>
                                <th className="px-4 py-3 text-center">Kullanım</th>
                                <th className="px-4 py-3 text-center">Durum</th>
                                <th className="px-4 py-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} data-testid={`coupon-row-${c.id}`} className="border-t border-slate-100 dark:border-slate-800/50">
                                    <td className="px-4 py-3"><code className="font-mono font-bold text-sky-600">{c.code}</code></td>
                                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{c.label || "-"}</td>
                                    <td className="px-4 py-3 text-right font-bold text-emerald-600">{c.discount_type === "percent" ? `${c.discount_value}%` : `${c.discount_value} TL`}</td>
                                    <td className="px-4 py-3 text-right text-slate-500">{c.min_booking_amount ? `${c.min_booking_amount} TL` : "-"}</td>
                                    <td className="px-4 py-3 text-center text-xs text-slate-500">{c.valid_from || "?"} → {c.valid_until || "∞"}</td>
                                    <td className="px-4 py-3 text-center text-xs"><TrendingUp className="inline h-3 w-3 mr-1" />{c.usage_count}/{c.usage_limit || "∞"}</td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : "bg-slate-100 text-slate-500"}`}>
                                            {c.active ? "Aktif" : "Pasif"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => setEditing(c)} data-testid={`edit-${c.id}`} className="p-1.5 rounded hover:bg-sky-50 dark:hover:bg-sky-950/40 text-sky-600"><Edit3 className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => handleDelete(c.id)} data-testid={`del-${c.id}`} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <CouponEditor coupon={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
        </AdminLayout>
    );
}

function CouponEditor({ coupon, onClose, onSave }) {
    const [data, setData] = useState({
        code: coupon.code || "",
        label: coupon.label || "",
        discount_type: coupon.discount_type || "percent",
        discount_value: coupon.discount_value ?? 10,
        min_booking_amount: coupon.min_booking_amount ?? 0,
        usage_limit: coupon.usage_limit ?? 0,
        valid_from: coupon.valid_from || "",
        valid_until: coupon.valid_until || "",
        active: coupon.active ?? true,
        id: coupon.id,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setData({ ...data, [k]: v });

    const save = async () => {
        setSaving(true);
        try { await onSave(data); }
        catch (e) { alert(e.response?.data?.detail || "Kayıt başarısız"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900">
                    <h3 className="font-display text-lg font-bold">{data.id ? "Kuponu Düzenle" : "Yeni Kupon"}</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">✕</button>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Kupon Kodu (otomatik büyük harf)">
                        <input data-testid="cp-code" value={data.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="YAZ25" className={`${INPUT} font-mono uppercase`} disabled={!!data.id} />
                    </Field>
                    <Field label="Açıklama (admin'e gözükür)"><input value={data.label} onChange={(e) => set("label", e.target.value)} placeholder="Yaz 2026 İndirimi" className={INPUT} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="İndirim Tipi">
                            <select data-testid="cp-type" value={data.discount_type} onChange={(e) => set("discount_type", e.target.value)} className={INPUT}>
                                <option value="percent">Yüzde (%)</option>
                                <option value="fixed">Sabit (TL)</option>
                            </select>
                        </Field>
                        <Field label="Değer"><input data-testid="cp-value" type="number" min="0" step="0.01" value={data.discount_value} onChange={(e) => set("discount_value", parseFloat(e.target.value) || 0)} className={INPUT} /></Field>
                    </div>
                    <Field label="Minimum sepet tutarı (TL, 0 = limit yok)"><input type="number" min="0" value={data.min_booking_amount} onChange={(e) => set("min_booking_amount", parseFloat(e.target.value) || 0)} className={INPUT} /></Field>
                    <Field label="Toplam kullanım limiti (0 = sınırsız)"><input type="number" min="0" value={data.usage_limit} onChange={(e) => set("usage_limit", parseInt(e.target.value, 10) || 0)} className={INPUT} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Başlangıç tarihi"><input type="date" value={data.valid_from} onChange={(e) => set("valid_from", e.target.value)} className={INPUT} /></Field>
                        <Field label="Bitiş tarihi"><input type="date" value={data.valid_until} onChange={(e) => set("valid_until", e.target.value)} className={INPUT} /></Field>
                    </div>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={!!data.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-sky-500" /> <span className="text-sm font-semibold">Aktif</span></label>
                </div>
                <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-slate-900">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-sm font-bold">İptal</button>
                    <button data-testid="cp-save" onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-50">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>{children}</label>;
}
